import { getCurrentTenantId } from 'src/stores/tenant';
import { getCurrentApiUrl } from '../apiUrlResolver';
import { UrlBuilderOptions } from './type';
import { MosError } from '../errors/MosError';

const TENANT_ID_PLACEHOLDERS = new Set(['replace-this']);

function normalizeTenantId(tenantId: string | null | undefined): string | null {
  if (typeof tenantId !== 'string') return null;

  const trimmedTenantId = tenantId.trim();
  if (!trimmedTenantId || TENANT_ID_PLACEHOLDERS.has(trimmedTenantId)) {
    return null;
  }

  return trimmedTenantId;
}

export const buildApiUrl = (path: string, options: UrlBuilderOptions) => {
  const { strategy, tenantId } = options;
  const apiUrl = getCurrentApiUrl();

  // Use provided tenantId or fallback to getCurrentTenantId
  const resolvedTenantId =
    normalizeTenantId(tenantId) ?? normalizeTenantId(getCurrentTenantId());

  // Shared endpoints (e.g., /shared/tenants) don't require tenant ID in the path
  // because the API resolves tenant from the authenticated user/token
  const isSharedEndpoint =
    typeof path === 'string' && path.startsWith('/shared');
  if (!resolvedTenantId && !isSharedEndpoint) {
    throw new MosError({ error: 'Tenant ID is required' });
  }

  // Normalize path to ensure it starts with a slash (except empty path)
  const ensureLeadingSlash = (p: string) =>
    p === '' || p.startsWith('/') ? p : `/${p}`;

  if (strategy === 'path' && !isSharedEndpoint) {
    // Handle empty path case for path-based routing
    const normalizedPath = ensureLeadingSlash(path);
    path =
      path === ''
        ? `/tenant/${resolvedTenantId}`
        : `/tenant/${resolvedTenantId}${normalizedPath}`;
  } else if (path !== '') {
    // For header-based routing or shared endpoints, ensure path starts with a slash
    path = ensureLeadingSlash(path);
  }

  // Normalize API URL to remove trailing slash to prevent double slashes
  const normalizedApiUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;

  // For empty path, return base URL with trailing slash for consistency
  return path === '' ? `${normalizedApiUrl}/` : `${normalizedApiUrl}${path}`;
};
